/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR344 G5: Real component editors for Components Studio.
 * 
 * Each editor controls formatting/content for a component.
 * Visibility is managed by Structure (Data Elements tab).
 * These editors write to components_json via onComponentsChange.
 */

import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Info } from "lucide-react";
import type { ComponentsConfig } from "./DealCardsTab";

// ================================================================
// HELPERS
// ================================================================

/** Read a component config sub-key safely */
function getConfig<T = Record<string, unknown>>(components: ComponentsConfig, key: string): T {
  return ((components as Record<string, unknown>)[key] ?? {}) as T;
}

/** Shared note about Structure-controlled visibility */
function VisibilityNote() {
  return (
    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded px-3 py-2">
      <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>Show/hide is controlled in Configure Data Elements → Structure.</span>
    </div>
  );
}

/** Common field row */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label className="text-xs font-medium whitespace-nowrap">{label}</Label>
      <div className="flex-1 max-w-[200px]">{children}</div>
    </div>
  );
}

// ================================================================
// HEADLINE EDITOR
// ================================================================

interface HeadlineConfig {
  source?: "headline_mechanic" | "title" | "custom_template";
  maxLines?: number;
  truncate?: boolean;
  textTransform?: "none" | "uppercase";
  showIcon?: boolean;
  prefix?: string;
  suffix?: string;
}

export function HeadlineEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<HeadlineConfig>(components, "headline");

  const update = (patch: Partial<HeadlineConfig>) => {
    onChange({ headline: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Source">
        <Select value={cfg.source ?? "headline_mechanic"} onValueChange={(v) => update({ source: v as HeadlineConfig["source"] })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="headline_mechanic" className="text-xs">Headline Mechanic</SelectItem>
            <SelectItem value="title" className="text-xs">Title</SelectItem>
            <SelectItem value="custom_template" className="text-xs">Custom Template</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Max Lines">
        <Select value={String(cfg.maxLines ?? 2)} onValueChange={(v) => update({ maxLines: parseInt(v) })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1" className="text-xs">1 line</SelectItem>
            <SelectItem value="2" className="text-xs">2 lines</SelectItem>
            <SelectItem value="3" className="text-xs">3 lines</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Truncation">
        <Switch checked={cfg.truncate !== false} onCheckedChange={(v) => update({ truncate: v })} />
      </Field>

      <Field label="Text Transform">
        <Select value={cfg.textTransform ?? "none"} onValueChange={(v) => update({ textTransform: v as "none" | "uppercase" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-xs">None</SelectItem>
            <SelectItem value="uppercase" className="text-xs">UPPERCASE</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Prefix">
        <Input
          value={cfg.prefix ?? ""}
          onChange={(e) => update({ prefix: e.target.value })}
          placeholder="e.g. 🎁"
          className="h-8 text-xs"
        />
      </Field>
    </div>
  );
}

// ================================================================
// DATES EDITOR
// ================================================================

interface DatesConfig {
  formatStyle?: "mmm_d_yyyy" | "d_mmm_yyyy" | "mmm_d";
  showYear?: boolean;
  prefix?: string;
  rangeSeparator?: "dash" | "to" | "endash";
  hideIfMissing?: boolean;
}

export function DatesEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<DatesConfig>(components, "dates");

  const update = (patch: Partial<DatesConfig>) => {
    onChange({ dates: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Date Format">
        <Select value={cfg.formatStyle ?? "mmm_d_yyyy"} onValueChange={(v) => update({ formatStyle: v as DatesConfig["formatStyle"] })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="mmm_d_yyyy" className="text-xs">Jan 15, 2025</SelectItem>
            <SelectItem value="d_mmm_yyyy" className="text-xs">15 Jan 2025</SelectItem>
            <SelectItem value="mmm_d" className="text-xs">Jan 15</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Show Year">
        <Switch checked={cfg.showYear !== false} onCheckedChange={(v) => update({ showYear: v })} />
      </Field>

      <Field label="Prefix Label">
        <Input
          value={cfg.prefix ?? ""}
          onChange={(e) => update({ prefix: e.target.value })}
          placeholder="e.g. Valid"
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Separator">
        <Select value={cfg.rangeSeparator ?? "endash"} onValueChange={(v) => update({ rangeSeparator: v as DatesConfig["rangeSeparator"] })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="endash" className="text-xs">– (en dash)</SelectItem>
            <SelectItem value="dash" className="text-xs">- (dash)</SelectItem>
            <SelectItem value="to" className="text-xs">to</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Hide if Missing">
        <Switch checked={cfg.hideIfMissing !== false} onCheckedChange={(v) => update({ hideIfMissing: v })} />
      </Field>
    </div>
  );
}

// ================================================================
// PROMO BADGES EDITOR
// ================================================================

interface PromoBadgesConfig {
  maxBadges?: number;
  layout?: "pills" | "compact";
  showIcons?: boolean;
  overflowLabel?: string;
}

export function PromoBadgesEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<PromoBadgesConfig>(components, "promo_badges");

  const update = (patch: Partial<PromoBadgesConfig>) => {
    onChange({ promo_badges: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <div className="space-y-2">
        <Label className="text-xs font-medium">Max Badges: {cfg.maxBadges ?? 3}</Label>
        <Slider
          value={[cfg.maxBadges ?? 3]}
          onValueChange={([v]) => update({ maxBadges: v })}
          min={1}
          max={5}
          step={1}
          className="w-full"
        />
      </div>

      <Field label="Layout">
        <Select value={cfg.layout ?? "pills"} onValueChange={(v) => update({ layout: v as "pills" | "compact" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pills" className="text-xs">Pills</SelectItem>
            <SelectItem value="compact" className="text-xs">Compact Chips</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Show Icons">
        <Switch checked={cfg.showIcons !== false} onCheckedChange={(v) => update({ showIcons: v })} />
      </Field>

      <Field label="Overflow Label">
        <Input
          value={cfg.overflowLabel ?? "+{n} more"}
          onChange={(e) => update({ overflowLabel: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>
    </div>
  );
}

// ================================================================
// DETAILS PILLS EDITOR
// ================================================================

interface DetailsPillsConfig {
  maxPills?: number;
  separator?: "dot" | "newline" | "pipe";
  prefix?: string;
  currencyDisplay?: "symbol" | "code";
}

export function DetailsPillsEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<DetailsPillsConfig>(components, "details_pills");

  const update = (patch: Partial<DetailsPillsConfig>) => {
    onChange({ details_pills: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <div className="space-y-2">
        <Label className="text-xs font-medium">Max Pills: {cfg.maxPills ?? 4}</Label>
        <Slider
          value={[cfg.maxPills ?? 4]}
          onValueChange={([v]) => update({ maxPills: v })}
          min={1}
          max={8}
          step={1}
          className="w-full"
        />
      </div>

      <Field label="Separator">
        <Select value={cfg.separator ?? "newline"} onValueChange={(v) => update({ separator: v as DetailsPillsConfig["separator"] })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newline" className="text-xs">New Line</SelectItem>
            <SelectItem value="dot" className="text-xs">Dot (·)</SelectItem>
            <SelectItem value="pipe" className="text-xs">Pipe (|)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Prefix">
        <Input
          value={cfg.prefix ?? ""}
          onChange={(e) => update({ prefix: e.target.value })}
          placeholder="Optional prefix"
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Currency">
        <Select value={cfg.currencyDisplay ?? "symbol"} onValueChange={(v) => update({ currencyDisplay: v as "symbol" | "code" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="symbol" className="text-xs">$ (symbol)</SelectItem>
            <SelectItem value="code" className="text-xs">CAD (code)</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

// ================================================================
// VALUE BOX EDITOR (CR450: Display mode controls)
// ================================================================

type ValueBoxMetric = "currency" | "percent";
type ValueBoxSecondaryMetric = "none" | "currency" | "percent" | "auto";

interface ValueBoxConfig {
  /** @deprecated Use primaryMetric instead */
  valueFormat?: "percent" | "currency";
  primaryMetric?: ValueBoxMetric;
  secondaryMetric?: ValueBoxSecondaryMetric;
  primaryRounding?: number;
  secondaryRounding?: number;
  rounding?: number;
  showIcon?: boolean;
  showApproxSign?: boolean;
}

export function ValueBoxEditor({
  components,
  onChange,
  labels,
  onLabelsChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
  labels?: Record<string, string>;
  onLabelsChange?: (update: Record<string, string>) => void;
}) {
  const cfg = getConfig<ValueBoxConfig>(components, "value_box");

  const update = (patch: Partial<ValueBoxConfig>) => {
    onChange({ value_box: { ...cfg, ...patch } } as any);
  };

  // CR450: Read label from canonical key with legacy fallback
  const labelText = labels?.value_box_title || labels?.value_title || labels?.effective_value_title || "Effective Value";

  const handleLabelChange = (value: string) => {
    if (onLabelsChange) {
      onLabelsChange({
        ...(labels ?? {}),
        value_box_title: value,
        value_title: value,
        effective_value_title: value,
      });
    }
  };

  // Resolve from new key or legacy valueFormat
  const primaryMetric: ValueBoxMetric = cfg.primaryMetric ?? cfg.valueFormat ?? "currency";
  const secondaryMetric: ValueBoxSecondaryMetric = cfg.secondaryMetric ?? "auto";
  const primaryRounding = cfg.primaryRounding ?? cfg.rounding ?? 0;
  const secondaryRounding = cfg.secondaryRounding ?? 0;

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Label Text">
        <div className="space-y-1">
          <Input
            value={labelText}
            onChange={(e) => handleLabelChange(e.target.value)}
            className="h-8 text-xs"
          />
          <span className="text-[9px] text-muted-foreground/50 font-mono">value_box_title</span>
        </div>
      </Field>

      <Field label="Primary shows">
        <Select value={primaryMetric} onValueChange={(v) => update({ primaryMetric: v as ValueBoxMetric, valueFormat: v as "percent" | "currency" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="currency" className="text-xs">$ amount</SelectItem>
            <SelectItem value="percent" className="text-xs">% back</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Secondary shows">
        <Select value={secondaryMetric} onValueChange={(v) => update({ secondaryMetric: v as ValueBoxSecondaryMetric })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto" className="text-xs">Auto (other metric)</SelectItem>
            <SelectItem value="none" className="text-xs">None</SelectItem>
            <SelectItem value="currency" className="text-xs">$ amount</SelectItem>
            <SelectItem value="percent" className="text-xs">% back</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Rounding (primary)">
        <Select value={String(primaryRounding)} onValueChange={(v) => update({ primaryRounding: parseInt(v), rounding: parseInt(v) })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0" className="text-xs">0 decimals</SelectItem>
            <SelectItem value="1" className="text-xs">1 decimal</SelectItem>
            <SelectItem value="2" className="text-xs">2 decimals</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Rounding (secondary)">
        <Select value={String(secondaryRounding)} onValueChange={(v) => update({ secondaryRounding: parseInt(v) })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="0" className="text-xs">0 decimals</SelectItem>
            <SelectItem value="1" className="text-xs">1 decimal</SelectItem>
            <SelectItem value="2" className="text-xs">2 decimals</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Show Icon">
        <Switch checked={cfg.showIcon !== false} onCheckedChange={(v) => update({ showIcon: v })} />
      </Field>

      <Field label="Show ≈ for secondary">
        <Switch checked={cfg.showApproxSign !== false} onCheckedChange={(v) => update({ showApproxSign: v })} />
      </Field>
    </div>
  );
}

// ================================================================
// SPEND BOX EDITOR (CR450: Label Text writes to template labels)
// ================================================================

interface SpendBoxConfig {
  currencyDisplay?: "symbol" | "code";
  showCurrencySymbol?: boolean;
  prefix?: string;
  suffix?: string;
}

export function SpendBoxEditor({
  components,
  onChange,
  labels,
  onLabelsChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
  labels?: Record<string, string>;
  onLabelsChange?: (update: Record<string, string>) => void;
}) {
  const cfg = getConfig<SpendBoxConfig>(components, "spend_box");

  const update = (patch: Partial<SpendBoxConfig>) => {
    onChange({ spend_box: { ...cfg, ...patch } } as any);
  };

  // CR450: Read from canonical key with legacy fallback
  const labelText = labels?.qualifying_spend_box_title || labels?.qualifying_spend_title || "Qualifying Spend";

  const handleLabelChange = (value: string) => {
    if (onLabelsChange) {
      onLabelsChange({
        ...(labels ?? {}),
        qualifying_spend_box_title: value,
        qualifying_spend_title: value, // dual-write legacy
      });
    }
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Label Text">
        <div className="space-y-1">
          <Input
            value={labelText}
            onChange={(e) => handleLabelChange(e.target.value)}
            className="h-8 text-xs"
          />
          <span className="text-[9px] text-muted-foreground/50 font-mono">qualifying_spend_box_title</span>
        </div>
      </Field>

      <Field label="Currency">
        <Select value={cfg.currencyDisplay ?? "symbol"} onValueChange={(v) => update({ currencyDisplay: v as "symbol" | "code" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="symbol" className="text-xs">$ (symbol)</SelectItem>
            <SelectItem value="code" className="text-xs">CAD (code)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Prefix">
        <Input
          value={cfg.prefix ?? ""}
          onChange={(e) => update({ prefix: e.target.value })}
          placeholder="e.g. Min."
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Suffix">
        <Input
          value={cfg.suffix ?? ""}
          onChange={(e) => update({ suffix: e.target.value })}
          placeholder="e.g. CAD"
          className="h-8 text-xs"
        />
      </Field>
    </div>
  );
}

// ================================================================
// REWARD BOX EDITOR (CR450: Label Text writes to template labels)
// ================================================================

interface RewardBoxConfig {
  currencyDisplay?: "symbol" | "code";
  valueType?: "currency" | "points";
  showIcon?: boolean;
}

export function RewardBoxEditor({
  components,
  onChange,
  labels,
  onLabelsChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
  labels?: Record<string, string>;
  onLabelsChange?: (update: Record<string, string>) => void;
}) {
  const cfg = getConfig<RewardBoxConfig>(components, "reward_box");

  const update = (patch: Partial<RewardBoxConfig>) => {
    onChange({ reward_box: { ...cfg, ...patch } } as any);
  };

  // CR450: Read from canonical key with legacy fallback
  const labelText = labels?.reward_box_title || labels?.reward_title || "Bonus Reward";

  const handleLabelChange = (value: string) => {
    if (onLabelsChange) {
      onLabelsChange({
        ...(labels ?? {}),
        reward_box_title: value,
        reward_title: value, // dual-write legacy
      });
    }
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Label Text">
        <div className="space-y-1">
          <Input
            value={labelText}
            onChange={(e) => handleLabelChange(e.target.value)}
            className="h-8 text-xs"
          />
          <span className="text-[9px] text-muted-foreground/50 font-mono">reward_box_title</span>
        </div>
      </Field>

      <Field label="Currency">
        <Select value={cfg.currencyDisplay ?? "symbol"} onValueChange={(v) => update({ currencyDisplay: v as "symbol" | "code" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="symbol" className="text-xs">$ (symbol)</SelectItem>
            <SelectItem value="code" className="text-xs">CAD (code)</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Value Type">
        <Select value={cfg.valueType ?? "currency"} onValueChange={(v) => update({ valueType: v as "currency" | "points" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="currency" className="text-xs">Currency</SelectItem>
            <SelectItem value="points" className="text-xs">Points</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Show Icon">
        <Switch checked={cfg.showIcon !== false} onCheckedChange={(v) => update({ showIcon: v })} />
      </Field>
    </div>
  );
}

// ================================================================
// CTA BUTTON EDITOR
// ================================================================

interface CTAButtonConfig {
  label?: string;
  variant?: "primary" | "secondary" | "outline";
  showIcon?: boolean;
}

export function CTAButtonEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<CTAButtonConfig>(components, "cta_button");

  const update = (patch: Partial<CTAButtonConfig>) => {
    onChange({ cta_button: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Button Label">
        <Input
          value={cfg.label ?? "View deal"}
          onChange={(e) => update({ label: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Style Variant">
        <Select value={cfg.variant ?? "primary"} onValueChange={(v) => update({ variant: v as CTAButtonConfig["variant"] })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="primary" className="text-xs">Primary</SelectItem>
            <SelectItem value="secondary" className="text-xs">Secondary</SelectItem>
            <SelectItem value="outline" className="text-xs">Outline</SelectItem>
          </SelectContent>
        </Select>
      </Field>

      <Field label="Show Icon">
        <Switch checked={cfg.showIcon !== false} onCheckedChange={(v) => update({ showIcon: v })} />
      </Field>
    </div>
  );
}

// ================================================================
// ACTIONS ROW EDITOR
// ================================================================

interface ActionsRowConfig {
  primaryLabel?: string;
  secondaryLabel?: string;
  showSecondary?: boolean;
  showExternalIcon?: boolean;
  layout?: "inline" | "stacked";
}

export function ActionsRowEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<ActionsRowConfig>(components, "actions");

  const update = (patch: Partial<ActionsRowConfig>) => {
    onChange({ actions: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Primary Label">
        <Input
          value={cfg.primaryLabel ?? "View deal"}
          onChange={(e) => update({ primaryLabel: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Show External Icon">
        <Switch checked={cfg.showExternalIcon !== false} onCheckedChange={(v) => update({ showExternalIcon: v })} />
      </Field>

      <Field label="Show Secondary">
        <Switch checked={cfg.showSecondary !== false} onCheckedChange={(v) => update({ showSecondary: v })} />
      </Field>

      {cfg.showSecondary !== false && (
        <Field label="Secondary Label">
          <Input
            value={cfg.secondaryLabel ?? "T&Cs"}
            onChange={(e) => update({ secondaryLabel: e.target.value })}
            className="h-8 text-xs"
          />
        </Field>
      )}

      <Field label="Layout">
        <Select value={cfg.layout ?? "inline"} onValueChange={(v) => update({ layout: v as "inline" | "stacked" })}>
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="inline" className="text-xs">Inline</SelectItem>
            <SelectItem value="stacked" className="text-xs">Stacked</SelectItem>
          </SelectContent>
        </Select>
      </Field>
    </div>
  );
}

// ================================================================
// FEEDBACK EDITOR
// ================================================================

interface FeedbackConfig {
  pendingLabel?: string;
  positiveLabel?: string;
  negativeLabel?: string;
}

export function FeedbackEditor({
  components,
  onChange,
}: {
  components: ComponentsConfig;
  onChange: (update: Partial<ComponentsConfig>) => void;
}) {
  const cfg = getConfig<FeedbackConfig>(components, "feedback");

  const update = (patch: Partial<FeedbackConfig>) => {
    onChange({ feedback: { ...cfg, ...patch } } as any);
  };

  return (
    <div className="space-y-4">
      <VisibilityNote />

      <Field label="Default Label">
        <Input
          value={cfg.pendingLabel ?? "You decide"}
          onChange={(e) => update({ pendingLabel: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Thumbs Up Label">
        <Input
          value={cfg.positiveLabel ?? "Worth it"}
          onChange={(e) => update({ positiveLabel: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>

      <Field label="Thumbs Down Label">
        <Input
          value={cfg.negativeLabel ?? "Not"}
          onChange={(e) => update({ negativeLabel: e.target.value })}
          className="h-8 text-xs"
        />
      </Field>
    </div>
  );
}

export default {
  HeadlineEditor,
  DatesEditor,
  PromoBadgesEditor,
  DetailsPillsEditor,
  ValueBoxEditor,
  SpendBoxEditor,
  RewardBoxEditor,
  CTAButtonEditor,
  ActionsRowEditor,
  FeedbackEditor,
};
